// Agenda e Oggi dello staff, calendario del cliente: date (giorni "AAAA-MM-GG" in ora di Roma, come le manda il
// backend), striscia dei giorni, griglia del mese, testi delle voci. Regole del backend: backend/agenda.py.
// I testi che vede anche il cliente prendono la sua lingua come ultimo parametro (di base l'italiano: lo staff,
// che non la passa, resta in italiano).
import { contesto, dataIn, PRINCIPALE } from '../i18n';

export const GIORNI_BREVI = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
export const GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
export const INTESTAZIONE_MESE = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
export const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
export const MESI_TITOLO = MESI.map((m) => m[0].toUpperCase() + m.slice(1));

export const TIPI = {
  visita: { nome: 'Visita', colore: 'visita' },
  telefonata: { nome: 'Telefonata', colore: 'telefonata' },
  scadenza: { nome: 'Scadenza', colore: 'scadenza' },
  rogito: { nome: 'Rogito', colore: 'rogito' },
  documento: { nome: 'Documenti', colore: 'documento' },
  compleanno: { nome: 'Compleanno', colore: 'compleanno' },
  nota: { nome: 'Nota', colore: 'nota' },
  altro: { nome: 'Attività', colore: 'altro' },
};

export const ESITI = {
  non_rispondono: 'non rispondono',
  richiamare: 'da richiamare',
  attesa_collega: 'aspettano il collega',
  attesa_disponibilita: 'in attesa di disponibilità',
  visita_fissata: 'visita fissata',
  gia_venduto: 'già venduto',
  non_collaborano: 'non collaborano',
  positivo: 'le è piaciuta',
  negativo: 'non fa per lui',
};

const due = (n) => String(n).padStart(2, '0');

// Il giorno con l'articolo: "l'8 settembre", "l'11 set", "l'1 agosto", "il 9 settembre". giorno = il numero o il testo
// che comincia con il numero ("8 settembre"). Una funzione sola per le pagine del cliente: prima "il 8 settembre",
// "il 11 settembre", "il 1 ago".
export const GIORNI_ELISIONE = [1, 8, 11];
const elide = (giorno) => GIORNI_ELISIONE.includes(parseInt(String(giorno), 10));
export const ilGiorno = (giorno) => `${elide(giorno) ? "l'" : 'il '}${giorno}`;
// Lo stesso giorno dopo "di": "dell'8 settembre", "dell'11 settembre", "del 9 settembre" (le righe di "In ritardo",
// che scrivevano "del 8 settembre" e "del 11 settembre").
export const delGiorno = (giorno) => `${elide(giorno) ? "dell'" : 'del '}${giorno}`;

export const isoGiorno = (d) => `${d.getFullYear()}-${due(d.getMonth() + 1)}-${due(d.getDate())}`;

export const daIso = (iso) => {
  const [a, m, g] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(a, m - 1, g);
};

// La finestra dell'agenda: cinque anni indietro e avanti, la stessa del server (backend/agenda.py, ANNI_AGENDA).
// Fuori di lì le date di JS traboccano: con "?giorno=9999-12-31" la striscia mostrava dieci volte "ven 31", perché
// aggiungiGiorni esce dall'anno a quattro cifre e daIso legge "10000-01-0" come il 31 dicembre 9999.
export const ANNI_AGENDA = 5;
export const GIORNI_FINESTRA = ANNI_AGENDA * 366;
const lontano = (d, oggi) => Math.abs(Math.round((d - daIso(oggi)) / 86400000)) > GIORNI_FINESTRA;

// Un giorno scritto nell'indirizzo ("?giorno=…") vale solo se è "AAAA-MM-GG" ed è una data che esiste davvero:
// "pippo" dava un titolo "NaN" e "Undefined NaN undefined", "2026-13-45" apriva febbraio 2027 (daIso si fida di
// new Date(anno, mese-1, giorno), che fa traboccare mesi e giorni). Con `oggi` deve stare anche nella finestra.
export const giornoValido = (iso, oggi = null) => {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [a, m, g] = iso.split('-').map(Number);
  const d = new Date(a, m - 1, g);
  if (d.getFullYear() !== a || d.getMonth() !== m - 1 || d.getDate() !== g) return false;
  return !oggi || !lontano(d, oggi);
};

// Un mese scritto nell'indirizzo ("?mese=AAAA-MM"), per la vista Mese: {anno, mese} (mese 0-11) o niente.
// Anche qui il controllo di andata e ritorno che ha `giornoValido`: "0000-01" passava la forma ma new Date(0, 0, 1)
// è il 1900, e il titolo diceva "Gennaio 0" sopra la griglia di gennaio 1900. Con `oggi` vale solo un mese che
// tocca la finestra dei cinque anni (basta un suo giorno dentro).
export const meseValido = (testo, oggi = null) => {
  if (typeof testo !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(testo)) return null;
  const anno = Number(testo.slice(0, 4));
  const mese = Number(testo.slice(5, 7)) - 1;
  const primo = new Date(anno, mese, 1);
  if (primo.getFullYear() !== anno || primo.getMonth() !== mese) return null;
  if (oggi && lontano(primo, oggi) && lontano(new Date(anno, mese + 1, 0), oggi)) return null;
  return { anno, mese };
};

export const isoMese = ({ anno, mese }) => `${anno}-${due(mese + 1)}`;

// Errori del server: il messaggio suo si mostra quando è scritto per chi lavora (400 "Date prima l'esito della
// visita", 403 "Non autorizzato", 404 "Voce non trovata"); per timeout e rete caduta un testo nostro con
// "Riprovate" (prima arrivava "Internal Server Error", in inglese e senza dire che si può riprovare).
//
// **Anche il 500**, dal 28/09/2026: il backend ora risponde ai guasti imprevisti con una frase in italiano
// (`server.py`, `errore_non_previsto`) e la scrive nei log. Prima quel messaggio si buttava via e restava
// «Non riuscito. Riprovate.»: aggiungere un referente a un'agenzia è rimasto rotto due settimane perché nessuno,
// né chi guardava lo schermo né chi guardava i log, poteva sapere che cosa fosse. Il 500 resta senza il motivo
// tecnico dentro (non si racconta com'è fatto il database), ma almeno dice che è colpa nostra e che è registrato.
export const CODICI_CON_MESSAGGIO = [400, 403, 404];
export const messaggioErrore = (e, generico = 'Non riuscito. Riprovate.') => {
  const risposta = e?.response;
  const dati = risposta?.data;
  // Il 500 si mostra **solo se la frase è nostra**: il gestore del backend la marca con `nostro: true`
  // (`server.py`, `errore_non_previsto`). Un «Internal Server Error» di Starlette o di un proxy davanti al sito è
  // inglese e non dice niente a nessuno: lì vince il testo nostro con "Riprovate".
  const nostroCinquecento = risposta?.status === 500 && dati?.nostro === true;
  const detail = CODICI_CON_MESSAGGIO.includes(risposta?.status) || nostroCinquecento ? dati?.detail : null;
  return typeof detail === 'string' && detail.trim() ? detail : generico;
};

// Il periodo chiesto al server non esce mai dalla finestra: `GET /api/agenda` risponde 400 a una data oltre i cinque
// anni (backend/agenda.py, `valida_data`), e al bordo la striscia dei 14 giorni e la griglia del mese la superavano
// di qualche giorno — l'agenda diceva "Non riesco a caricare l'agenda" dove bastava una griglia vuota.
export const nellaFinestra = (iso, oggi) => {
  if (!oggi) return iso;
  const quanti = differenzaGiorni(iso, oggi);
  if (quanti > GIORNI_FINESTRA) return aggiungiGiorni(oggi, GIORNI_FINESTRA);
  if (quanti < -GIORNI_FINESTRA) return aggiungiGiorni(oggi, -GIORNI_FINESTRA);
  return iso;
};

export const aggiungiGiorni = (iso, n) => {
  const d = daIso(iso);
  d.setDate(d.getDate() + n);
  return isoGiorno(d);
};

export const differenzaGiorni = (a, b) => Math.round((daIso(a) - daIso(b)) / 86400000);

// I campi data (<input type="date">) non devono lasciar scegliere quello che il server rifiuta: la finestra è la
// stessa dell'agenda, cinque anni indietro e avanti (backend/agenda.py, `valida_data`: 400 "Data troppo lontana:
// mettete un giorno entro 5 anni"). I campi avevano solo il `min` di oggi, o niente: la pagina prometteva
// "→ giovedì 15 giu 2045 alle 10:00" con Salva acceso, e il rifiuto arrivava dopo il clic.
export const limitiData = (oggi) => ({ min: aggiungiGiorni(oggi, -GIORNI_FINESTRA), max: aggiungiGiorni(oggi, GIORNI_FINESTRA) });
// Una data che il server non prende: fuori dalla finestra, o scritta storta (il `max` del campo non ferma tutto —
// si può scrivere l'anno a mano, e certi browser lasciano il valore fuori limite). Vuoto non è "lontana".
export const dataLontana = (iso, oggi) => Boolean(iso) && !giornoValido(iso, oggi);

export const weekend = (iso) => [0, 6].includes(daIso(iso).getDay());

// Le agenzie la domenica sono chiuse: una telefonata non si programma mai di domenica. Lo fa già il server per il
// richiamo automatico (backend/telefonate.py, `domani_stessa_ora`); qui serve per dire la data vera nel riquadro
// dell'esito e per le scelte di "Richiamare".
export const domenica = (iso) => daIso(iso).getDay() === 0;
export const saltaDomenica = (iso) => (domenica(iso) ? aggiungiGiorni(iso, 1) : iso);

// Il mese di una data come si scrive accanto al giorno: "ott", e con l'anno quando non è quello in corso ("gen 2027",
// come nel blocco 1). `meseSeDiverso` lo dà solo quando la data non è del mese in corso: "6 MAR" per il 6 ottobre si
// leggeva come un 6 già passato, o come marzo.
export const meseConAnno = (iso, oggi) => {
  const d = daIso(iso);
  const o = daIso(oggi);
  return `${MESI[d.getMonth()].slice(0, 3)}${d.getFullYear() !== o.getFullYear() ? ` ${d.getFullYear()}` : ''}`;
};

export const meseSeDiverso = (iso, oggi) => {
  const d = daIso(iso);
  const o = daIso(oggi);
  return d.getMonth() === o.getMonth() && d.getFullYear() === o.getFullYear() ? null : meseConAnno(iso, oggi);
};

// Una data corta dentro una frase: "17 set", "17 set 2027" (l'anno solo quando non è quello in corso)
export const dataBreve = (iso, oggi) => `${daIso(iso).getDate()} ${meseConAnno(iso, oggi)}`;

// Dove ha guardato la ricerca dell'agenda. La frase dei risultati diceva sempre "in tutti i giorni", ma i giorni
// guardati sono quelli che decide il server e che rimanda indietro con i risultati (`dal`/`al`, backend/agenda.py):
// quando la finestra non è tutta l'agenda, "tutti i giorni" promette più di quello che si è cercato — quello che sta
// più in là non esce e nessuno lo dice. Se la finestra copre l'agenda intera (cinque anni di qua e di là, la stessa
// di `limitiData`) "tutti i giorni" è vero e si scrive così; se no si dice fin dove si è guardato.
export const GIORNI_ANNO = 365;
export const tuttiIGiorni = (dal, al, oggi) => Boolean(dal && al && oggi)
  && differenzaGiorni(oggi, dal) >= ANNI_AGENDA * GIORNI_ANNO
  && differenzaGiorni(al, oggi) >= ANNI_AGENDA * GIORNI_ANNO;
export const doveHoCercato = (finestra, oggi) => {
  const dal = finestra?.dal;
  const al = finestra?.al;
  // senza finestra (il server non l'ha mandata) non si inventa niente: si dice quello che si diceva prima
  if (!dal || !al || !giornoValido(dal) || !giornoValido(al) || tuttiIGiorni(dal, al, oggi)) return 'in tutti i giorni';
  return `dal ${dataBreve(dal, oggi)} al ${dataBreve(al, oggi)}`;
};

// Il giorno dentro una frase: "oggi", "domani", "lunedì 21", "lunedì 2 gen 2027" (mese e anno solo quando servono).
export const giornoTesto = (iso, oggi) => {
  const diff = differenzaGiorni(iso, oggi);
  if (diff === 0) return 'oggi';
  if (diff === 1) return 'domani';
  const d = daIso(iso);
  const mese = meseSeDiverso(iso, oggi);
  return `${GIORNI[d.getDay()]} ${d.getDate()}${mese ? ` ${mese}` : ''}`;
};

// "domani alle 10:00", "lunedì 21 alle 10:00": quando si richiama, come lo scrive il riquadro dell'esito
export const quandoRichiamo = (iso, ora, oggi) => `${giornoTesto(iso, oggi)}${ora ? ` alle ${ora}` : ''}`;

// La ricerca dell'agenda guarda un anno prima e uno dopo, e i compleanni il server li crea uno per anno: la stessa
// persona compariva due volte ("28 FEB · Compleanno di Олена Шевченко"), senza anno e con una delle due già passata.
// Di ogni persona resta un compleanno solo — il primo che deve ancora arrivare, o l'ultimo se sono tutti passati —
// al posto che aveva nell'elenco.
export const compleanniUnaVolta = (voci, oggi) => {
  const chiave = (v) => `${v.cliente_id || ''}|${v.titolo || ''}`;
  const tenuti = {};
  const futuro = (v) => String(v.data) >= oggi;
  (voci || []).forEach((v) => {
    if (v.tipo !== 'compleanno') return;
    const k = chiave(v);
    const prima = tenuti[k];
    if (!prima) { tenuti[k] = v; return; }
    if (futuro(v) !== futuro(prima)) { if (futuro(v)) tenuti[k] = v; return; }
    if (futuro(v) ? v.data < prima.data : v.data > prima.data) tenuti[k] = v;
  });
  return (voci || []).filter((v) => v.tipo !== 'compleanno' || tenuti[chiave(v)] === v);
};

// "Oggi, domenica 13" · "Domani, lunedì 14" · "Giovedì 17 settembre"
export const titoloGiorno = (iso, oggi, lingua = PRINCIPALE) => {
  const d = daIso(iso);
  const { t } = contesto(lingua);
  const scritta = (opzioni) => dataIn(lingua, d, { ...opzioni, timeZone: undefined }); // il giorno è già quello di Roma
  const base = lingua === PRINCIPALE ? `${GIORNI[d.getDay()]} ${d.getDate()}` : scritta({ weekday: 'long', day: 'numeric' });
  const diff = differenzaGiorni(iso, oggi);
  if (diff === 0) return { prefisso: t('calendario.oggi_prefisso'), resto: base };
  if (diff === 1) return { prefisso: t('calendario.domani_prefisso'), resto: base };
  if (diff === -1) return { prefisso: t('calendario.ieri_prefisso'), resto: base };
  const intero = lingua === PRINCIPALE ? `${base} ${MESI[d.getMonth()]}` : scritta({ weekday: 'long', day: 'numeric', month: 'long' });
  return { prefisso: '', resto: `${intero[0].toUpperCase()}${intero.slice(1)}` };
};

// Striscia di 14 giorni: due prima del giorno scelto, undici dopo
export const giorniStriscia = (centro, prima = 2, totale = 14) =>
  Array.from({ length: totale }, (_, i) => aggiungiGiorni(centro, i - prima));

// Griglia del mese: settimane intere da lunedì a domenica
export const grigliaMese = (anno, mese) => {
  const primo = new Date(anno, mese, 1);
  const inizio = new Date(primo);
  inizio.setDate(1 - ((primo.getDay() + 6) % 7));
  const ultimo = new Date(anno, mese + 1, 0);
  const fine = new Date(ultimo);
  fine.setDate(ultimo.getDate() + (6 - ((ultimo.getDay() + 6) % 7)));
  const celle = [];
  for (let d = new Date(inizio); d <= fine; d.setDate(d.getDate() + 1)) {
    celle.push({ iso: isoGiorno(d), giorno: d.getDate(), fuori: d.getMonth() !== mese });
  }
  return celle;
};

export const perGiorno = (voci) => voci.reduce((acc, v) => { (acc[v.data] = acc[v.data] || []).push(v); return acc; }, {});

// Nella cella del mese: al massimo due voci, poi "+N"
export const vociCella = (voci, max = 2) => ({ mostrate: voci.slice(0, max), altre: Math.max(0, voci.length - max) });

// Maiuscoletto del tipo: "Telefonata · 2° tentativo", "Visita · fatta"
export const etichettaTipo = (voce) => {
  const pezzi = [TIPI[voce.tipo]?.nome || 'Attività'];
  if (voce.stato === 'fatta') pezzi.push(voce.esito && ESITI[voce.esito] ? `fatta · ${ESITI[voce.esito]}` : 'fatta');
  else if (voce.stato === 'annullata') pezzi.push('annullata');
  else if (voce.tipo === 'telefonata' && voce.tentativi > 0) pezzi.push(`${voce.tentativi + 1}° tentativo`);
  else if (voce.in_ritardo) pezzi.push('in ritardo');
  return pezzi.join(' · ');
};

// Riga sotto il titolo: per chi, telefono, nota
export const rigaVoce = (voce) => {
  const pezzi = [];
  if (voce.tipo === 'visita') {
    if (voce.cliente_breve) pezzi.push(voce.cliente_breve);
    if (voce.agenzia?.nome) pezzi.push(voce.agenzia.nome);
  } else {
    if (voce.cliente_breve) pezzi.push(`per ${voce.cliente_breve}`);
    if (voce.tipo === 'telefonata' && voce.telefono) pezzi.push(voce.telefono);
  }
  if (voce.note && voce.stato !== 'fatta') pezzi.push(voce.note);
  return pezzi.join(' · ');
};

// L'ordine di una voce nella giornata, lo stesso del server (backend/agenda.py, `ordina`): prima i compleanni e le
// scadenze di tutto il giorno, poi le ore, in fondo le cose senza ora.
export const chiaveOrdine = (v) => [
  (v.tutto_il_giorno || !v.ora) && ['compleanno', 'scadenza'].includes(v.tipo) ? 0 : 1,
  v.ora || '99:99',
  v.titolo || '',
];

const confronta = (a, b) => {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] < b[i]) return -1;
    if (a[i] > b[i]) return 1;
  }
  return 0;
};

// Oggi: le cose dello stesso cliente insieme quando sono più di una. Il gruppo sta dove sta la sua voce più presto —
// la prima che ha un'ora — non dove compare la prima dell'elenco: con una giornata piena (il compleanno del cliente,
// che è di tutto il giorno, e la sua visita alle 16) il gruppo saliva in cima e le cose degli altri, delle 9 e delle
// 10, finivano dopo le 16. Le voci senza ora (compleanni e scadenze in testa, il resto in fondo, come le mette il
// server) non decidono mai dove va il gruppo: contano solo se il gruppo non ha nessuna voce con l'ora.
export const raggruppaPerCliente = (voci) => {
  const quante = {};
  voci.forEach((v) => { if (v.cliente_id) quante[v.cliente_id] = (quante[v.cliente_id] || 0) + 1; });
  const gruppi = {};
  const blocchi = [];
  const abbassa = (gruppo, v) => {
    const chiave = chiaveOrdine(v);
    if (v.ora && !gruppo.conOra) { gruppo.conOra = true; gruppo.chiave = chiave; return; }
    if (Boolean(v.ora) === gruppo.conOra && confronta(chiave, gruppo.chiave) < 0) gruppo.chiave = chiave;
  };
  voci.forEach((v) => {
    if (!v.cliente_id || quante[v.cliente_id] < 2) { blocchi.push({ tipo: 'voce', voce: v, chiave: chiaveOrdine(v) }); return; }
    if (!gruppi[v.cliente_id]) {
      gruppi[v.cliente_id] = { tipo: 'cliente', cliente: { id: v.cliente_id, nome: v.cosa?.cliente?.nome || v.cliente_breve || 'Cliente' },
        voci: [], chiave: chiaveOrdine(v), conOra: Boolean(v.ora) };
      blocchi.push(gruppi[v.cliente_id]);
    } else {
      abbassa(gruppi[v.cliente_id], v);
    }
    gruppi[v.cliente_id].voci.push(v);
  });
  return blocchi.slice().sort((a, b) => confronta(a.chiave, b.chiave));
};

// La telefonata raggruppata: più case dello stesso cliente con la stessa agenzia da chiamare lo stesso giorno alla
// stessa ora diventano UNA voce sola (backend/agenda.py, raggruppa_per_agenzia), con l'elenco delle case in
// `dettaglio.case` [{immobile_id, indirizzo, sottotipo}]. Per le voci di una casa sola torna [] e non cambia niente.
export const caseDellaVoce = (voce) => {
  const case_ = (voce?.dettaglio?.case || []).filter((c) => c && c.immobile_id);
  return case_.length >= 2 ? case_ : [];
};

// Le stesse case nella forma che vuole EsitoTelefonata (`case={[{id, indirizzo, titolo}]}`): sta qui e non in
// ogni pagina, se no una pagina la passa e un'altra se la dimentica e l'esito finisce su una casa sola.
export const caseEsito = (voce) => caseDellaVoce(voce)
  .map((c) => ({ id: c.immobile_id, indirizzo: c.indirizzo, titolo: c.indirizzo }));

export const prezzoBreve = (prezzo) => {
  if (typeof prezzo !== 'number' || !prezzo) return null;
  return `${Math.round(prezzo).toLocaleString('it-IT')} €`;
};

// Ora come la mostra la linea del tempo: "10:30", oppure "—" per le cose di tutto il giorno
export const oraVoce = (voce) => voce.ora || '—';

// La riga "adesso" va prima della prima voce con l'ora dopo adesso
export const indiceAdesso = (voci, oraAdesso) => {
  const i = voci.findIndex((v) => v.ora && v.ora > oraAdesso);
  return i === -1 ? voci.length : i;
};

// "1 cosa", "3 cose" e — quando chi chiama lo dice — il caso zero con le parole invece del numero: l'intestazione di
// "Oggi" scriveva "Sandro, 0 cose prima di sera", con sotto "Niente in agenda per oggi." (la stessa cosa detta due
// volte, una in italiano e una no). Senza la terza parola resta com'era ("0 voci", "0 cose"), perché altrove il
// numero accanto a un elenco vuoto va bene.
export const quantiTesto = (n, una, tante, nessuna = null) => (n === 0 && nessuna ? nessuna : `${n} ${n === 1 ? una : tante}`);

// Home del cliente: la disponibilità si chiede se è vuota o non aggiornata da più di 14 giorni.
// fase (chiave della fase del percorso): in Mutuo e Rogito l'offerta è accettata e non si fanno più visite, quindi
// «Quando potete fare visite?» e «La vostra disponibilità è ancora questa?» non compaiono (restano nel profilo);
// quella segnata dallo staff sì (l'ha fatto qualcuno di noi: il cliente la controlla).
export const FASI_SENZA_VISITE = ['mutuo', 'rogito'];
export const GIORNI_DISPONIBILITA_VECCHIA = 14;
export const disponibilitaDaChiedere = (disp, adesso = new Date(), fase = null) => {
  if (disp?.aggiornata_da?.tipo === 'staff') return 'dallo_staff';  // segnata da noi: il cliente controlla che sia giusta
  if (FASI_SENZA_VISITE.includes(fase)) return null;
  const vuota = !disp || (!Object.keys(disp.settimana || {}).length && !Object.values(disp.settimane || {}).some((g) => Object.keys(g || {}).length));
  if (vuota) return 'vuota';
  if (!disp.aggiornata_il) return 'vecchia';
  return differenzaGiorni(isoGiorno(adesso), String(disp.aggiornata_il).slice(0, 10)) > GIORNI_DISPONIBILITA_VECCHIA ? 'vecchia' : null;
};

// Sotto la griglia: "aggiornata da Karolina il 14 set" / "aggiornata dal cliente il 14 set" (staff);
// "segnata da Karolina il 14 set" / "aggiornata da voi il 14 set" (cliente)
export const testoAggiornata = (disp, delloStaff = false, lingua = PRINCIPALE) => {
  const iso = disp?.aggiornata_alle || disp?.aggiornata_il;
  if (!iso) return null;
  const d = String(iso).length > 10 ? new Date(/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`) : daIso(String(iso).slice(0, 10));
  const { t } = contesto(lingua);
  const quando = lingua === PRINCIPALE ? ilGiorno(`${d.getDate()} ${MESI[d.getMonth()].slice(0, 3)}`)
    : dataIn(lingua, d, { day: 'numeric', month: 'short', timeZone: undefined });
  const chi = disp.aggiornata_da;
  if (!chi) return t('calendario.disp.aggiornata', { quando });
  if (chi.tipo === 'staff') {
    return delloStaff ? `aggiornata da ${chi.nome} ${quando}` : t('calendario.disp.segnata_da', { nome: chi.nome, quando });
  }
  return delloStaff ? `aggiornata dal cliente ${quando}` : t('calendario.disp.aggiornata_da_voi', { quando });
};

const FASCE_BREVI = ['mattina', 'pomeriggio', 'sera'];
const GIORNI_RIASSUNTO = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];

// "mer mattina · ven mattina e sera" (la settimana tipo, o quella di adesso se non vale sempre)
export const riassuntoGriglia = (disp, adesso = new Date(), lingua = PRINCIPALE) => {
  if (!disp) return '';
  const { t } = contesto(lingua);
  let griglia = disp.settimana || {};
  if (disp.ogni_settimana === false) {
    const oggi = isoGiorno(adesso);
    const lunedi = aggiungiGiorni(oggi, -((daIso(oggi).getDay() + 6) % 7));
    griglia = (disp.settimane || {})[lunedi] || {};
  }
  const giorno = (i) => (lingua === PRINCIPALE ? GIORNI_RIASSUNTO[i]
    : dataIn(lingua, daIso(aggiungiGiorni('2024-01-01', i)), { weekday: 'short', timeZone: undefined })); // 1/1/2024 è un lunedì
  return GIORNI_RIASSUNTO.map((g, i) => {
    const fasce = (griglia[String(i)] || []).map((f) => (FASCE_BREVI.includes(f) ? t(`calendario.fasce_brevi.${f}`) : f));
    if (!fasce.length) return null;
    const elenco = fasce.length > 1 ? `${fasce.slice(0, -1).join(', ')} ${t('comune.e')} ${fasce[fasce.length - 1]}` : fasce[0];
    return `${giorno(i)} ${elenco}`;
  }).filter(Boolean).join(' · ');
};
